/* eslint-disable react/no-unescaped-entities */
import {
  Container,
  Heading,
  Box,
  Input,
  Grid,
  Text,
  VStack,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Icon,
  Flex,
  Collapse,
} from "@chakra-ui/react";
import type { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useMemo, useState, useCallback } from "react";
import {
  FiSearch,
  FiHelpCircle,
  FiFolder,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";

import type { HelpPostProps } from "lib/components/help/Help";
import HelpCard from "lib/components/help/HelpCard";
import Banner from "lib/components/home/Banner";
import IntercomHelp from "lib/components/IntercomHelp";

const BASE_QUERY = `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/items/help?sort=-date_created`;
const DEFAULT_CATEGORY = "general";

// Format category slug to display name (e.g., "device_setup" -> "Device Setup")
const formatCategoryName = (category: string): string => {
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface APIProps {
  data: HelpPostProps[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getStaticProps: GetStaticProps = async (context) => {
  const res = await fetch(BASE_QUERY);
  const posts = await res.json();

  return {
    props: { data: posts.data },
    revalidate: 10,
  };
};

const Help = ({ data }: APIProps) => {
  const [dta, setDta] = useState(data);
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  const isCategoryExpanded = useCallback(
    (category: string) => expandedCategories[category] === true,
    [expandedCategories]
  );

  const search = async (text: string) => {
    let res;
    if (text === "") {
      res = await fetch(`${BASE_QUERY}`);
    } else {
      res = await fetch(
        `${BASE_QUERY}&filter[_or][0][title][_contains]=${text}&filter[_or][1][description][_contains]=${text}`
      );
    }
    const posts = await res.json();

    setDta(posts.data);
  };

  const bgGradient = useColorModeValue(
    "linear(to-b, neutral.50, white)",
    "linear(to-b, neutral.900, neutral.800)"
  );
  const searchBg = useColorModeValue("white", "neutral.700");
  const searchBorder = useColorModeValue("neutral.200", "neutral.600");
  const iconColor = useColorModeValue("brand.500", "brand.300");
  const subtitleColor = useColorModeValue("neutral.600", "neutral.400");
  const categoryBg = useColorModeValue("neutral.50", "neutral.800");
  const categoryIconBg = useColorModeValue("orange.100", "orange.900");
  const headerHoverBg = useColorModeValue("neutral.100", "neutral.700");

  // Group articles by category
  const groupedArticles = useMemo(() => {
    const groups: Record<string, HelpPostProps[]> = {};
    dta.forEach((article) => {
      const category = article.Category || DEFAULT_CATEGORY;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(article);
    });
    // Sort categories alphabetically, but keep "general" at the end
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      if (a === DEFAULT_CATEGORY) return 1;
      if (b === DEFAULT_CATEGORY) return -1;
      return a.localeCompare(b);
    });
    return sortedCategories.map((category) => ({
      category,
      displayName: formatCategoryName(category),
      articles: groups[category],
    }));
  }, [dta]);

  return (
    <>
      <NextSeo title="Help" description="Adiona Alert Help Center" />
      <Banner />

      {/* Hero Section */}
      <Box
        bgGradient={bgGradient}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <Container maxW="4xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FiHelpCircle} boxSize={12} color={iconColor} />
            <Heading
              fontWeight={700}
              fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
              letterSpacing="tight"
              lineHeight="shorter"
            >
              How can we help?
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={subtitleColor}
              maxW="2xl"
            >
              Search our knowledge base or browse articles below to find the
              answers you need.
            </Text>

            {/* Modern Search Input */}
            <Box w="100%" maxW="xl" pt={4}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none" h="full">
                  <Icon as={FiSearch} color="neutral.400" boxSize={5} />
                </InputLeftElement>
                <Input
                  placeholder="Search for articles..."
                  bg={searchBg}
                  border="1px solid"
                  borderColor={searchBorder}
                  borderRadius="xl"
                  h={14}
                  pl={12}
                  fontSize="md"
                  _placeholder={{ color: "neutral.400" }}
                  _hover={{
                    borderColor: "brand.300",
                  }}
                  _focus={{
                    borderColor: "brand.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
                  }}
                  onChange={(e) => search(e.target.value)}
                />
              </InputGroup>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Articles Section */}
      <Container maxW="6xl" py={{ base: 10, md: 16 }} px={{ base: 4, md: 8 }}>
        <Text
          fontSize="sm"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing="wider"
          color={subtitleColor}
          mb={8}
        >
          {dta.length} {dta.length === 1 ? "Article" : "Articles"} Available
        </Text>

        <VStack spacing={6} align="stretch">
          {groupedArticles.map(({ category, displayName, articles }) => (
            <Box key={category}>
              {/* Category Header - Clickable */}
              <Flex
                align="center"
                py={3}
                px={4}
                mx={-4}
                borderRadius="lg"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ bg: headerHoverBg }}
                onClick={() => toggleCategory(category)}
                role="button"
                aria-expanded={isCategoryExpanded(category)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleCategory(category);
                  }
                }}
              >
                <Icon
                  as={
                    isCategoryExpanded(category)
                      ? FiChevronDown
                      : FiChevronRight
                  }
                  boxSize={5}
                  color={subtitleColor}
                  mr={2}
                  transition="transform 0.2s"
                />
                <Flex
                  w={8}
                  h={8}
                  bg={categoryIconBg}
                  borderRadius="lg"
                  align="center"
                  justify="center"
                  mr={3}
                >
                  <Icon as={FiFolder} boxSize={4} color={iconColor} />
                </Flex>
                <Heading
                  as="h2"
                  fontSize={{ base: "lg", md: "xl" }}
                  fontWeight={600}
                >
                  {displayName}
                </Heading>
                <Text
                  ml={3}
                  fontSize="sm"
                  color={subtitleColor}
                  fontWeight={500}
                >
                  ({articles.length})
                </Text>
              </Flex>

              {/* Collapsible Category Articles Grid */}
              <Collapse in={isCategoryExpanded(category)} animateOpacity>
                <Box
                  bg={categoryBg}
                  borderRadius="xl"
                  p={{ base: 4, md: 6 }}
                  mt={3}
                >
                  <Grid
                    templateColumns={{
                      base: "repeat(1, 1fr)",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={{ base: 4, md: 5 }}
                  >
                    {articles.map((helpData, i) => (
                      <HelpCard
                        title={helpData.title}
                        description={helpData.description}
                        date_created={helpData.date_created}
                        slug={helpData.slug}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${category}-${i}`}
                        content={helpData.content}
                        date_updated={helpData.date_updated}
                        Category={helpData.Category}
                      />
                    ))}
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          ))}
        </VStack>

        <Box pt={12}>
          <IntercomHelp />
        </Box>
      </Container>
    </>
  );
};

export default Help;
